import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { GamerPermissionScreen } from '../components/GamerPermissionScreen'
import { GameControlComponent } from '../components/GameControlComponent'

interface GameCenterPageProps {
  className?: string
}

/**
 * GameCenterPage - Entry point for game selection
 * Allows users to choose between starting with introduction or entering lobby directly
 * Context-aware: navigates to facilitator or gamer routes based on current path
 * Now includes camera/mic permission flow for gamers
 * Features astronaut background and new GameControlComponent
 */
export const GameCenterFacilitatorPage: React.FC<GameCenterPageProps> = ({ className = '' }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showPermissionScreen, setShowPermissionScreen] = useState(false)
  const [pendingGameType, setPendingGameType] = useState<'tutorial' | 'lobby' | null>(null)

  // Detect if we're in facilitator or gamer context
  const isFacilitatorContext = location.pathname.startsWith('/facilitator')
  const baseRoute = isFacilitatorContext ? '/facilitator' : '/gamer'

  const handleStartTutorial = () => {
    if (isFacilitatorContext) {
      // Facilitators don't need permission screen
      navigate(`${baseRoute}/play-game-with-introduction`)
    } else {
      // Gamers need permission screen first
      setPendingGameType('tutorial')
      setShowPermissionScreen(true)
    }
  }

  const handleEnterLobby = () => {
    if (isFacilitatorContext) {
      // Facilitators don't need permission screen
      navigate(`${baseRoute}/play-game-without-introduction`)
    } else {
      // Gamers need permission screen first
      setPendingGameType('lobby')
      setShowPermissionScreen(true)
    }
  }

  const handlePermissionsGranted = () => {
    if (pendingGameType === 'tutorial') {
      navigate(`${baseRoute}/play-game-with-introduction`)
    } else if (pendingGameType === 'lobby') {
      navigate(`${baseRoute}/play-game-without-introduction`)
    }
  }

  // Show permission screen for gamers only
  if (showPermissionScreen && !isFacilitatorContext) {
    return <GamerPermissionScreen onPermissionsGranted={handlePermissionsGranted} className={className} />
  }

  return (
    <Box
      className={className}
      sx={{
        // Full viewport height with centered content
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        // Background image with astronaut scene
        backgroundImage: 'url(/assets/img/game_center_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* GameControlComponent centered in the content area */}
      <GameControlComponent mode="facilitator" onEnterLobby={handleEnterLobby} onStartTutorial={handleStartTutorial} />
    </Box>
  )
}
